import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, Skeleton } from 'antd';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { objectToQuery } from '../../utils/Utils';
import { COURSES_CATEGORY_URL, TEACHER_URL } from '../../constants/Url';
import { 
  setEducationCourseCategoryAction, 
  setEducationTeachersAction 
} from '../../modules/dataWebsite/actions';

export default function OnlineClassListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Connect mapped states to Redux
  const courseCategories = useSelector((state) => state.dataWebsiteReducer?.courseCategoryData) || [];
  const teachers = useSelector((state) => state.dataWebsiteReducer?.courseTeacherData) || [];

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [pageSize, setPageSize] = useState(8);

  const fetchCourseCategories = async () => {
    try {
      const res = await httpClient.get(objectToQuery(COURSES_CATEGORY_URL))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200 && res.data) {
        setEducationCourseCategoryAction([{ _id: 'all', name: '全部' }, ...res.data]);
      }
    } catch (error) {
      console.error('fetchCourseCategories error', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const categoryIdFilter = activeTab === 'all' ? '' : activeTab;
      const payload = {
        keyword: '',
        categoryId: categoryIdFilter,
        pageNo: currentPage,
        pageSize: pageSize
      };
      const res = await httpClient.get(objectToQuery(TEACHER_URL, payload))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        setEducationTeachersAction(res.data || []);
        setTotalTeachers(res.total || res.data?.length || 0);
      } else {
        setEducationTeachersAction([]);
        setTotalTeachers(0);
      }
    } catch (error) {
      console.error('fetchTeachers error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseCategories();
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [activeTab, currentPage, pageSize]);



  return (
    <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">

      {/* ── Top Header Banner Section ── */}
      <div className="w-full py-10 px-4 text-center bg-gradient-to-r from-[#4d9dec] via-[#637cfb] to-[#a380f9] text-white">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide m-0 text-white mb-2">
          {t('text_tech_edu')} / {t("menu_online_class")}
        </h1>
        {/* <p className="text-[13px] sm:text-[14px] opacity-90 m-0">
          {t('适合传统与青年文化，先进的技术和实战教程')}
        </p> */}
      </div>

      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-10">

        {/* Category selector / Tabs */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-200 pb-3 mb-8 gap-4">
          <h2 className="text-[18px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0 whitespace-nowrap shrink-0">
            {t('menu_online_class')}
          </h2>
          <div className="flex items-center justify-start sm:justify-end gap-6 sm:gap-10 overflow-x-auto [&::-webkit-scrollbar]:hidden flex-1 w-full sm:w-auto">
            <div className="flex gap-4 sm:gap-6 text-[13px] sm:text-[14px] font-semibold w-max">
              {courseCategories.map((tab) => {
                const isActive = activeTab === (tab._id || tab.id);
                return (
                  <div
                    key={tab._id || tab.id}
                    onClick={() => { setActiveTab(tab._id || tab.id); setCurrentPage(1); setPageSize(8); }}
                    className={`cursor-pointer pb-2 relative transition-colors whitespace-nowrap duration-200 shrink-0 ${
                      isActive ? 'text-black font-bold' : 'text-gray-400 hover:text-black'
                    }`}
                  >
                    {t(tab.name)}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#df97fb] via-[#7887fd] to-[#50c7f8] rounded-full" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tutors Grid Layout */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 flex flex-col gap-4 aspect-[3/4]">
                <div className="w-full flex-1 rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                  <Skeleton.Image className="!w-full !h-full" active />
                </div>
                <div className="flex justify-center">
                  <Skeleton.Input active size="small" style={{ width: '60%', height: '16px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : teachers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {teachers.map((item) => (
              <div
                key={item._id || item.id}
                onClick={() => navigate(`/online-class/${item.courseId || item._id || item.id}`)}
                className="group relative overflow-hidden rounded-lg shadow-sm hover:shadow-md cursor-pointer aspect-[3/4] bg-gray-50 flex flex-col justify-end"
              >
                {/* Background profile image */}
                <img
                  src={item.picUrl || item.imageUrl || item.image || item.coverImage || item.imagePath}
                  alt={item.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />

                {/* Gradient bottom overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent z-0" />

                {/* Profile detail text */}
                <div className="p-4 z-10 w-full text-center">
                  <h4 className="text-white text-xs sm:text-sm font-bold m-0 tracking-wide">
                    {item.name}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('text_no_teacher')} />
        )}

        {/* Pagination Component */}
        {(loading || totalTeachers > 0 || teachers.length > 0) && (
          <div className="flex justify-end mt-12">
            <CustomPagination
              current={currentPage}
              pageSize={pageSize}
              total={loading ? 8 : (totalTeachers || teachers.length || 0)}
              onChange={(page, limit) => {
                if (limit !== pageSize) {
                  setPageSize(limit);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(page);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
